import { NavLink, useLocation, useNavigate } from 'react-router-dom';
import { Avatar, Menu, MenuButton, MenuList, MenuItem, useColorMode } from '@chakra-ui/react';
import classes from './Navbar.module.scss';
import logo from '../../../media/logo/logo.png';
import AuthContext from '../../../store/auth-context';
import React, { useContext, useState, useRef, useEffect } from 'react';
import NotificationDropdown from './NotificationDropdown';

const navLinks = [
    { name: 'Dashboard', link: '/' },
    { name: 'Auction', link: '/auction' },
    { name: 'Dealer Stock', link: '/dealer-stock' },
    // Everything the member owns - saved cars, inquiries, inspections,
    // purchases and invoices, from the auctions and from dealer stock.
    { name: 'My Garage', link: '/garage' },
    { name: 'Guide', link: '/guide' },
];

const Navbar = ({ notifications, onNotificationClick, onMarkAllRead }) => {
    const { colorMode, toggleColorMode } = useColorMode();
    const auth = useContext(AuthContext);
    const location = useLocation();
    const navigate = useNavigate();
    const isHomePage = location.pathname === '/';

    const [showDropdown, setShowDropdown] = useState(false);
    const [showProfileMenu, setShowProfileMenu] = useState(false);
    const notificationRef = useRef(null);

    const isAdmin = auth.userData?.userRole === "administrator";
    const unreadCount = notifications ? notifications.filter(notif => !notif.isRead).length : 0;

    const handleLogout = () => {
        auth.logout();
        navigate('/login');
    };

    const handleProfile = () => {
        navigate('/profile');
    };

    const handleManagement = () => {
        navigate('/management');
    };

    const toggleNotificationDropdown = (e) => {
        e.stopPropagation();
        setShowDropdown(prev => !prev);
    };

    const handleProfileMenuOpen = () => setShowProfileMenu(true);
    const handleProfileMenuClose = () => setShowProfileMenu(false);

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (notificationRef.current && !notificationRef.current.contains(event.target)) {
                setShowDropdown(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    let navbarBackground = 'white';
    let showNav;

    switch (location.pathname) {
        case '/':
            navbarBackground = 'transparent';
            showNav = (
                <nav className={classes.DashNav}>
                    {navLinks.map((navlink) => (
                        <NavLink
                            key={navlink.name}
                            to={navlink.link}
                            className={({ isActive }) =>
                                isActive ? classes.ActiveDashLink : ''
                            }
                        >
                            {navlink.name}
                        </NavLink>
                    ))}
                </nav>
            );
            break;
        default:
            showNav = (
                <nav className={classes.NonDashNav}>
                    {navLinks.map((navlink) => (
                        <NavLink
                            key={navlink.name}
                            to={navlink.link}
                            className={({ isActive }) => {
                                if (
                                    location.pathname.includes('/detail') &&
                                    navlink.name === 'Auction'
                                ) {
                                    return classes.ActiveNonDashLink;
                                }
                                return isActive ? classes.ActiveNonDashLink : '';
                            }}
                        >
                            {navlink.name}
                        </NavLink>
                    ))}
                </nav>
            );
    }

    return (
        <>
            {(showDropdown || showProfileMenu) && (
                <div className={classes.overlay} />
            )}
            <div
                className={`${classes.Navbar} ${
                    !isHomePage ? classes.invertedNav : ''
                }`}
                style={{ backgroundColor: navbarBackground }}
            >
                <div className={classes.LogoDiv} onClick={() => navigate('/')}>
                    <img src={logo} alt="LOGO" />
                </div>
                <div className={classes.NavDiv}>{showNav}</div>
                <div className={classes.LoginDiv}>
                    {/* Notification Button */}
                    <div className={classes.notificationWrapper} ref={notificationRef}>
                        <button
                            className={`${classes.notificationBtn} ${
                                !isHomePage ? classes.inverted : ''
                            }`}
                            onClick={toggleNotificationDropdown}
                        >
                            <i className="fas fa-bell"></i>
                            {unreadCount > 0 && (
                                <span className={classes.notificationBadge}>
                                    {unreadCount > 9 ? '9+' : unreadCount}
                                </span>
                            )}
                        </button>
                        {showDropdown && (
                            <NotificationDropdown 
                                notifications={notifications}
                                onClose={() => setShowDropdown(false)}
                                onNotificationClick={onNotificationClick}
                                onMarkAllRead={onMarkAllRead}
                                isMobile={false}
                            />
                        )}
                    </div>
                    <div>
                        <button 
                            className={`${classes.darkModeBtn} ${
                                !isHomePage ? classes.inverted : ''
                            }`}
                            onClick={toggleColorMode}
                        >
                            {colorMode === "light" ? <i className="fas fa-moon"></i> : <i className="fas fa-sun"></i> }
                            
                        </button>
                    </div>    
                    {/* Profile Menu */}
                    <Menu
                        isOpen={showProfileMenu}
                        onOpen={handleProfileMenuOpen}
                        onClose={handleProfileMenuClose}
                    >
                        <MenuButton className={classes.LoginDiv}>
                            <Avatar
                                name={`${auth?.userData?.firstName || ''} ${auth?.userData?.lastName || ''}`}
                                src={auth?.userData?.image || "https://mmpubimages.s3.us-west-1.amazonaws.com/defaultprofile.png"}
                                onError={(e) => {
                                    console.log("Error loading profile image:", e);
                                    e.target.src = "https://mmpubimages.s3.us-west-1.amazonaws.com/defaultprofile.png";
                                }}
                            />
                        </MenuButton>
                        <MenuList className={classes.menuList}>
                        {isAdmin && <MenuItem className={classes.menuItem} onClick={handleManagement}>
                            <i className="fas fa-server"></i>
                            Management
                        </MenuItem> }
                            <MenuItem
                                className={classes.menuItem}
                                onClick={handleProfile}
                            >
                                <i className="fas fa-user"></i>
                                Profile
                            </MenuItem>
                            <MenuItem
                                className={classes.menuItem}
                                onClick={handleLogout}
                            >
                                <i className="fas fa-sign-out-alt"></i>
                                Sign Out
                            </MenuItem>
                        </MenuList>
                    </Menu>
                </div>
            </div>
        </>
    );
};

export default Navbar;
 